/**
 * Rubans du fond « Vague » : de longues bandes de soie translucides qui ondulent. Aucun dessin ici, seulement du calcul
 * (donc testable) : les paramètres d'un ruban (tirés d'une graine) et le tracé SVG de sa forme.
 *
 * Un ruban a un bord haut et un bord bas qui ondulent chacun de leur côté (somme de deux sinusoïdes), donc une épaisseur qui
 * varie le long du ruban. Les ondulations ont un nombre ENTIER de cycles par période : le tracé se répète sans raccord, et le
 * composant le fait glisser d'une période en boucle.
 */

export const PERIODE = 1000;       // largeur d'une période, dans le repère du tracé
export const HAUTEUR = 112;        // hauteur du repère (celle du bandeau)
export const PAS = 20;             // distance entre deux points du tracé

/** Générateur pseudo-aléatoire à graine (mulberry32). */
function aleatoire(graine) {
    let a = graine >>> 0;
    return () => {
        a = (a + 0x6D2B79F5) >>> 0;
        let t = a;
        t = Math.imul(t ^ (t >>> 15), t | 1);
        t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}

/** Paramètres du ruban n° `indice` sur `total` : même indice, mêmes paramètres, quel que soit le nombre de rubans voisins. */
export function ruban(indice, total) {
    const r = aleatoire(9100 + indice * 7919);
    const choisir = liste => liste[Math.floor(r() * liste.length)];
    return {
        centre: HAUTEUR / 2 + (total > 1 ? (indice / (total - 1) - 0.5) * 16 : 0),
        k1: choisir([1, 2]), a1: 14 + r() * 14, p1: r() * Math.PI * 2,
        k2: choisir([2, 3, 4]), a2: 3 + r() * 7, p2: r() * Math.PI * 2,
        k3: choisir([1, 2, 3]), epaisseur: 16 + r() * 14, variation: 6 + r() * 8, p3: r() * Math.PI * 2,
        opacite: 0.22 + r() * 0.3,
        derive: 24 + r() * 22,         // secondes pour glisser d'une période, à la vitesse ×1
        houle: 5 + r() * 5,            // secondes d'un aller-retour vertical
        amplitudeHoule: 3 + r() * 6,   // pixels
    };
}

/** Ordonnées du bord haut et du bord bas du ruban à l'abscisse `x` (dans le repère de PERIODE de large). */
export function bords(p, x) {
    const u = (2 * Math.PI * x) / PERIODE;
    const haut = p.centre + p.a1 * Math.sin(p.k1 * u + p.p1) + p.a2 * Math.sin(p.k2 * u + p.p2);
    const epais = Math.max(6, p.epaisseur + p.variation * Math.sin(p.k3 * u + p.p3));
    return {haut, bas: haut + epais};
}

/** Tracé SVG fermé du ruban sur `periodes` périodes. */
export function chemin(p, periodes = 2) {
    const fin = PERIODE * periodes, xs = [];
    for (let x = 0; x <= fin; x += PAS) xs.push(x);
    const haut = xs.map(x => `${x},${bords(p, x).haut.toFixed(1)}`);
    const bas = xs.slice().reverse().map(x => `${x},${bords(p, x).bas.toFixed(1)}`);
    return `M${haut.join("L")}L${bas.join("L")}Z`;
}
